import { useMutation, useQuery } from '@apollo/client';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import { CompanyEventStatusEnumType, InvestorRightEnumType } from '__generated__/graphql';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import DetailsPanel from 'components/DetailsPanel';
import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';

import Badge from 'primitives/Badge';
import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';
import SlideOver from 'primitives/SlideOver';

import companyEventStatusToReadable from 'utils/enums/company-event-status-type-to-readable';
import companyEventTypeToReadable from 'utils/enums/company-event-type-to-readable';
import investorRightToReadable from 'utils/enums/investor-right-to-readable';

const COMPANY_EVENT_QUERY = gql(`
  query getCompanyEvent($id: ID!, $syndicateId: ID!) {
    companyEvent(id: $id, syndicateId: $syndicateId) {
      id
      label
      type
      eventDate
      isAuthorizationRequired
      authorizationStatus
      description
      notes
      investorRightsAdded
      investorRightsRemoved
      company {
        name
      }
      documents {
        id
        url
        name
        type
      }
      shareAssets {
        shareAsset {
          id
          deal {
            schemeName
          }
          investorRights
          documents {
            id
            name
            url
          }
        }
      }
      convertibleAssets {
        convertibleAsset {
          id
          deal {
            schemeName
          }
          investorRights
          documents {
            id
            name
            url
          }
        }
      }
      deals {
        deal {
          id
          schemeName
        }
      }
    }
  }
`);

const CONTACT_SUPPORT_FOR_COMPANY_EVENT_MUTATION = gql(`
  mutation ContactSupportForCompanyEvent(
    $id: ID!
    $subject: String!
    $message: String!
  ) {
    contactSupportForCompanyEvent(
      id: $id
      subject: $subject
      message: $message
    )
  }
`);

const AUTHORIZE_COMPANY_EVENT_MUTATION = gql(`
  mutation AuthorizeCompanyEventAsLead($id: ID!, $authorization: CompanyEventStatusEnumType!, $syndicateId: ID!) {
    authorizeCompanyEventAsLead(id: $id, authorization: $authorization, syndicateId: $syndicateId) 
  }
`);

export default function CompanyEventSlideOver({
  open = false,
  eventId,
  onClose,
}: {
  open: boolean;
  eventId: string;
  onClose: () => void;
}) {
  const { syndicateId } = useParams<{ syndicateId: string }>() as { syndicateId: string };

  const { loading, error, data, refetch } = useQuery(COMPANY_EVENT_QUERY, {
    variables: {
      id: eventId,
      syndicateId,
    },
    skip: !open,
  });

  const [authorizeCompanyEvent, { loading: authorizeLoading }] = useMutation(
    AUTHORIZE_COMPANY_EVENT_MUTATION,
    {
      onCompleted: () => {
        refetch();
      },
    }
  );

  function renderContent() {
    if (loading) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const event = data.companyEvent;

    return (
      <div>
        <div className="mt-12" />

        {event.isAuthorizationRequired ? (
          <div className="mb-12">
            <div className="mb-4">
              <h3 className="font-bold text-lg">Respond to this event</h3>
              {(event.authorizationStatus === CompanyEventStatusEnumType.Authorized ||
                event.authorizationStatus === CompanyEventStatusEnumType.Rejected) && (
                <h4 className="text-sm text-gray-500">You have responded to this event.</h4>
              )}
            </div>
            <div className="flex w-full space-x-4">
              <Button
                variant="secondary"
                onClick={() => {
                  authorizeCompanyEvent({
                    variables: {
                      id: event.id,
                      authorization: CompanyEventStatusEnumType.Rejected,
                      syndicateId,
                    },
                  });
                }}
                disabled={
                  authorizeLoading ||
                  event.authorizationStatus === CompanyEventStatusEnumType.Authorized ||
                  event.authorizationStatus === CompanyEventStatusEnumType.Rejected
                }
                className="w-full"
              >
                {event.authorizationStatus === CompanyEventStatusEnumType.Rejected ? (
                  <span className="text-black text-xs mr-2">❌</span>
                ) : (
                  ''
                )}
                {event.authorizationStatus === CompanyEventStatusEnumType.Rejected
                  ? 'Rejected'
                  : 'Reject'}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  authorizeCompanyEvent({
                    variables: {
                      id: event.id,
                      authorization: CompanyEventStatusEnumType.Authorized,
                      syndicateId,
                    },
                  });
                }}
                className="w-full"
                disabled={
                  authorizeLoading ||
                  event.authorizationStatus === CompanyEventStatusEnumType.Authorized ||
                  event.authorizationStatus === CompanyEventStatusEnumType.Rejected
                }
              >
                {event.authorizationStatus === CompanyEventStatusEnumType.Authorized ? (
                  <span className="text-black text-xs mr-2">✓</span>
                ) : (
                  ''
                )}
                {event.authorizationStatus === CompanyEventStatusEnumType.Authorized
                  ? 'Authorized'
                  : 'Authorize'}
              </Button>
            </div>
          </div>
        ) : null}

        <DetailsPanel
          displayHorizontally
          title={data?.companyEvent.label}
          data={[
            {
              label: 'Company',
              value: event.company.name,
            },
            {
              label: 'Type',
              type: 'CUSTOM',
              value: companyEventTypeToReadable(event.type).label,
              customValue: () => <Badge {...companyEventTypeToReadable(event.type)} />,
            },
            {
              label: 'Date',
              value: event.eventDate,
            },
            event.isAuthorizationRequired
              ? {
                  label: 'Status',
                  type: 'CUSTOM',
                  value: companyEventStatusToReadable(event.authorizationStatus),
                  customValue: () => (
                    <Badge
                      label={companyEventStatusToReadable(event.authorizationStatus).label}
                      color={companyEventStatusToReadable(event.authorizationStatus).color}
                      customIcon={
                        companyEventStatusToReadable(event.authorizationStatus).customIcon
                      }
                    />
                  ),
                }
              : {},
            {
              label: 'Description',
              value: event.description,
            },
            {
              label: 'Notes',
              value: event.notes,
            },
            {
              label: 'Changes in Investor Rights',
              value: event.investorRightsAdded || event.investorRightsRemoved,
              type: 'CUSTOM',
              customValue: () => (
                <div className="flex flex-col gap-y-2 w-full">
                  {event.investorRightsAdded && event.investorRightsAdded.length > 0 && (
                    <div className="mb-2">
                      <p className="text-sm font-medium mb-1">Added Rights:</p>
                      <div className="flex flex-col gap-2">
                        {event.investorRightsAdded.map((right, index) => (
                          <div key={`added-${index}`} className="mr-4">
                            <Badge
                              {...investorRightToReadable(right || InvestorRightEnumType.None)}
                              size="sm"
                              color="green"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {event.investorRightsRemoved && event.investorRightsRemoved.length > 0 && (
                    <div>
                      <p className="text-sm font-medium mb-1">Removed Rights:</p>
                      <div className="flex flex-col gap-2">
                        {event.investorRightsRemoved.map((right, index) => (
                          <div key={`removed-${index}`} className="mr-4">
                            <Badge
                              {...investorRightToReadable(right || InvestorRightEnumType.None)}
                              size="sm"
                              color="red"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {!event.investorRightsAdded && !event.investorRightsRemoved && (
                    <p className="text-sm text-gray-500">No changes</p>
                  )}
                </div>
              ),
            },
            {
              label: 'Documents',
              value: event.documents,
              type: 'CUSTOM',
              customValue: () => (
                <div>
                  {event.documents && event.documents.length > 0 && (
                    <div className="flex flex-wrap gap-4">
                      {event.documents.map(doc => (
                        <a
                          key={doc!.id}
                          href={doc!.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                          onClick={e => e.stopPropagation()}
                        >
                          <DocumentIcon className="h-4 w-4" />
                          {doc!.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />

        {/* Show assets and their current rights */}
        <div className="border rounded-lg p-4 mt-12">
          <h3 className="font-semibold text-lg mb-4">Schemes affected by this event</h3>
          <div className="flex flex-col gap-4">
            {/* List share assets */}
            {event.shareAssets?.map(asset => (
              <div key={asset?.shareAsset?.id} className="p-4 border bg-slate-100/50 rounded-lg">
                <p className="font-medium mb-1">{asset?.shareAsset?.deal?.schemeName}</p>
                <p className="text-xs text-gray-500 mb-3">
                  Investor rights may not be up to date. Please refer documents for accurate rights.
                </p>
                <div className="flex flex-row flex-wrap gap-2 items-center">
                  <p className="text-sm text-gray-500">Current rights: </p>
                  {asset?.shareAsset?.investorRights?.map(right => (
                    <Badge
                      {...investorRightToReadable(right || InvestorRightEnumType.None)}
                      size="sm"
                    />
                  ))}
                </div>
                <div className="mt-3">
                  <p className="text-sm text-gray-500 mb-1">Related documents: </p>
                  <div className="flex flex-wrap gap-4">
                    {asset?.shareAsset?.documents?.map(doc => (
                      <a
                        key={doc!.id}
                        href={doc!.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                        onClick={e => e.stopPropagation()}
                      >
                        <DocumentIcon className="h-4 w-4" />
                        {doc!.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            {/* List convertible assets */}
            {event.convertibleAssets?.map(asset => (
              <div
                key={asset?.convertibleAsset?.id}
                className="p-4 border bg-slate-100/50 rounded-lg"
              >
                <p className="font-medium mb-1">{asset?.convertibleAsset?.deal?.schemeName}</p>
                <p className="text-xs text-gray-500 mb-3">
                  Investor rights may not be up to date. Please refer documents for accurate rights.
                </p>
                <div className="flex flex-row flex-wrap gap-2 items-center">
                  <p className="text-sm text-gray-500">Current rights: </p>
                  {asset?.convertibleAsset?.investorRights?.map((right, index) => (
                    <Badge
                      {...investorRightToReadable(right || InvestorRightEnumType.None)}
                      key={`${right}-${index}`}
                      size="sm"
                    />
                  ))}
                </div>
                <div className="mt-3">
                  <p className="text-sm text-gray-500 mb-1">Related documents: </p>
                  <div className="flex flex-wrap gap-4">
                    {asset?.convertibleAsset?.documents?.map(doc => (
                      <a
                        key={doc!.id}
                        href={doc!.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                        onClick={e => e.stopPropagation()}
                      >
                        <DocumentIcon className="h-4 w-4" />
                        {doc!.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            {/* List deals */}
            {event.deals?.map(deal => (
              <div key={deal?.deal?.id} className="p-4 border bg-slate-100/50 rounded-lg">
                <p className="font-medium mb-1">{deal?.deal?.schemeName}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="border rounded-lg p-4 mt-12">
          <ContactSupportForCompanyEvent eventId={eventId} />
        </div>
      </div>
    );
  }

  return (
    <SlideOver open={open} onClose={onClose} title={'Event Details'}>
      {renderContent()}
    </SlideOver>
  );
}

const ContactSupportForCompanyEvent: FC<{ eventId: string }> = ({ eventId }) => {
  const [contactSupport, { loading, error }] = useMutation(
    CONTACT_SUPPORT_FOR_COMPANY_EVENT_MUTATION
  );
  const [showSuccess, setShowSuccess] = useState(false);

  return (
    <>
      <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-1">
        Have additional questions?
      </h3>
      <p className="text-base tracking-tight text-gray-700 mb-5">
        Write to us about any questions you have before responding to this event.
      </p>
      {!showSuccess ? (
        <FormPanel
          loading={loading}
          error={error}
          onSubmit={data => {
            contactSupport({
              variables: {
                id: eventId,
                subject: data.subject,
                message: `${data.message}. Message sent for company ID ${eventId}`,
              },
            }).then(() => {
              setShowSuccess(true);
            });
          }}
        >
          <FormInput
            fullWidth
            type="text"
            fieldName="subject"
            label="Subject"
            defaultValue=""
            validators={{
              required: true,
            }}
          />
          <FormInput
            fullWidth
            type="textarea"
            fieldName="message"
            label="Describe the clarification you need."
            defaultValue=""
            validators={{
              required: true,
            }}
          />
        </FormPanel>
      ) : (
        <div className="bg-green-50 sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-semibold leading-6 text-gray-900">
              Thank you for reporting the issue
            </h3>
            <div className="mt-2 max-w-xl text-sm text-gray-700">
              <p>
                Our team will review the issue and get back to you as soon as possible. We
                appreciate your patience.
              </p>
            </div>
            <div className="mt-5">
              <button
                type="button"
                className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                onClick={() => {
                  setShowSuccess(false);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
